/**
 * Megamenu component: tabbed panel navigation with trigger management.
 * Handles open/close, tab switching, mobile overlay, and keyboard nav.
 */

const MEGAMENU_BREAKPOINT = 1100;

const MEGAMENU_SELECTORS = {
  trigger: ".js-megamenu-trigger",
  triggerLabel: ".js-megamenu-trigger-label",
  button: "[data-megamenu-button]",
  panel: "[data-megamenu-panel]",
  activeButton: "[data-megamenu-button].megamenu-item--active",
  stage: "[data-megamenu-stage]",
  back: "[data-megamenu-back]",
  underlay: ".megamenu__underlay",
};

const MEGAMENU_CLASSES = {
  active: "is-active",
  panelHidden: "megamenu-panel--hidden",
  buttonActive: "megamenu-item--active",
  stageOverlayActive: "megamenu__stage--overlay-active",
  bodyFreeze: "freezeBody",
};

function megamenuDebounce(fn, wait) {
  let t;
  return function (...args) {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

class MegamenuComponent extends HTMLElement {
  constructor() {
    super();
    this.buttons = [];
    this.panels = [];
    this.stage = null;
    this.backBtn = null;
    this.underlay = null;
    this._boundHandleClick = this.handleButtonClick.bind(this);
    this._boundHandleBack = this.handleBackClick.bind(this);
    this._boundHandleResize = megamenuDebounce(
      this.handleResize.bind(this),
      100
    );
    this._boundHandleKeydown = this.handleKeydown.bind(this);
    this._boundHandleTriggerClick = this.handleTriggerClick.bind(this);
    this._boundHandleEscape = this.handleEscape.bind(this);
    this._boundHandleUnderlayClick = this.close.bind(this);
  }

  connectedCallback() {
    this.getElements();
    if (!this.buttons.length || !this.panels.length) return;

    this.syncPanelVisibility();
    this.bindEvents();
  }

  disconnectedCallback() {
    this.unbindEvents();
  }

  get isMobile() {
    return window.innerWidth < MEGAMENU_BREAKPOINT;
  }

  get isOpen() {
    return this.classList.contains(MEGAMENU_CLASSES.active);
  }

  getElements() {
    this.buttons = [...this.querySelectorAll(MEGAMENU_SELECTORS.button)];
    this.panels = [...this.querySelectorAll(MEGAMENU_SELECTORS.panel)];
    this.stage = this.querySelector(MEGAMENU_SELECTORS.stage);
    this.backBtn = this.querySelector(MEGAMENU_SELECTORS.back);
    this.underlay = this.querySelector(MEGAMENU_SELECTORS.underlay);
  }

  bindEvents() {
    this.buttons.forEach((btn) => {
      btn.addEventListener("click", this._boundHandleClick);
      btn.addEventListener("keydown", this._boundHandleKeydown);
    });
    this.backBtn?.addEventListener("click", this._boundHandleBack);
    this.underlay?.addEventListener("click", this._boundHandleUnderlayClick);
    window.addEventListener("resize", this._boundHandleResize);
    document.addEventListener("click", this._boundHandleTriggerClick);
    document.addEventListener("keyup", this._boundHandleEscape);
  }

  unbindEvents() {
    this.buttons.forEach((btn) => {
      btn.removeEventListener("click", this._boundHandleClick);
      btn.removeEventListener("keydown", this._boundHandleKeydown);
    });
    this.backBtn?.removeEventListener("click", this._boundHandleBack);
    this.underlay?.removeEventListener("click", this._boundHandleUnderlayClick);
    window.removeEventListener("resize", this._boundHandleResize);
    document.removeEventListener("click", this._boundHandleTriggerClick);
    document.removeEventListener("keyup", this._boundHandleEscape);
  }

  // ── Open / Close ──────────────────────────────────────────────────────

  open() {
    this.classList.add(MEGAMENU_CLASSES.active);
    this.updateTriggers(true);
    document.body.classList.add(MEGAMENU_CLASSES.bodyFreeze);
    this.syncPanelVisibility();

    requestAnimationFrame(() => {
      const target = this.buttons[0];
      if (target) target.focus();
    });
  }

  close() {
    this.classList.remove(MEGAMENU_CLASSES.active);
    this.updateTriggers(false);
    document.body.classList.remove(MEGAMENU_CLASSES.bodyFreeze);

    const trigger = document.querySelector(MEGAMENU_SELECTORS.trigger);
    trigger?.focus();
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  updateTriggers(isOpen) {
    const triggers = document.querySelectorAll(MEGAMENU_SELECTORS.trigger);

    triggers?.forEach((trigger) => {
      trigger.classList.toggle(MEGAMENU_CLASSES.active, isOpen);

      const label = trigger.querySelector(MEGAMENU_SELECTORS.triggerLabel);
      const openText = trigger.dataset.megamenuOpenText;
      const closedText = trigger.dataset.megamenuClosedText;

      if (label && (openText || closedText)) {
        label.textContent = isOpen
          ? openText || closedText
          : closedText || openText;
      }
    });
  }

  // ── Panel state ─────────────────────────────────────────────────────

  hideAllPanels() {
    this.panels.forEach((panel) => {
      panel.classList.add(MEGAMENU_CLASSES.panelHidden);
      panel.setAttribute("aria-hidden", "true");
      panel.setAttribute("tabindex", "-1");
    });
  }

  showPanel(panel) {
    panel.classList.remove(MEGAMENU_CLASSES.panelHidden);
    panel.setAttribute("aria-hidden", "false");
    panel.removeAttribute("tabindex");
  }

  clearActiveButton() {
    this.buttons.forEach((b) => {
      b.classList.remove(MEGAMENU_CLASSES.buttonActive);
      b.setAttribute("aria-selected", "false");
      b.setAttribute("tabindex", "-1");
    });
  }

  setActiveButton(button) {
    this.clearActiveButton();
    button.classList.add(MEGAMENU_CLASSES.buttonActive);
    button.setAttribute("aria-selected", "true");
    button.setAttribute("tabindex", "0");
  }

  getPanelById(panelId) {
    return panelId
      ? this.querySelector(
          `${MEGAMENU_SELECTORS.panel}[data-panel-id="${panelId}"]`
        )
      : null;
  }

  setStageOverlay(active) {
    if (!this.stage) return;
    this.stage.classList.toggle(MEGAMENU_CLASSES.stageOverlayActive, active);
  }

  syncPanelVisibility() {
    this.hideAllPanels();

    if (this.isMobile) {
      this.clearActiveButton();
      this.setStageOverlay(false);
      return;
    }

    const activeButton = this.querySelector(MEGAMENU_SELECTORS.activeButton);
    const panelId = activeButton?.getAttribute("data-panel-id");
    const targetPanel = this.getPanelById(panelId);

    if (targetPanel) {
      this.showPanel(targetPanel);
    } else if (this.panels[0]) {
      this.showPanel(this.panels[0]);
      this.setActiveButton(this.buttons[0]);
    }
  }

  handleResize() {
    if (this.isMobile) {
      const hasActivePanel = this.panels.some(
        (p) => !p.classList.contains(MEGAMENU_CLASSES.panelHidden)
      );
      this.setStageOverlay(hasActivePanel);
    } else {
      this.setStageOverlay(false);
      this.syncPanelVisibility();
    }
  }

  // ── Event handlers ──────────────────────────────────────────────────

  handleTriggerClick(event) {
    if (event.target.closest(MEGAMENU_SELECTORS.trigger)) {
      event.preventDefault();
      this.toggle();
    }
  }

  handleEscape(event) {
    if (event.key === "Escape" && this.isOpen) {
      this.close();
    }
  }

  handleButtonClick(event) {
    const button = event.currentTarget;
    const panelId = button.getAttribute("data-panel-id");

    if (!panelId) return;
    if (button.classList.contains(MEGAMENU_CLASSES.buttonActive)) return;

    const targetPanel = this.getPanelById(panelId);
    if (!targetPanel) return;

    this.hideAllPanels();
    this.showPanel(targetPanel);
    this.setActiveButton(button);

    if (this.isMobile) {
      this.setStageOverlay(true);
    }
  }

  handleBackClick() {
    this.hideAllPanels();
    this.clearActiveButton();
    this.setStageOverlay(false);

    if (this.buttons[0]) {
      this.buttons[0].focus();
    }
  }

  handleKeydown(event) {
    const currentIndex = this.buttons.indexOf(event.currentTarget);
    if (currentIndex === -1) return;

    let nextIndex;

    switch (event.key) {
      case "ArrowDown":
      case "ArrowRight":
        event.preventDefault();
        nextIndex = (currentIndex + 1) % this.buttons.length;
        break;
      case "ArrowUp":
      case "ArrowLeft":
        event.preventDefault();
        nextIndex =
          (currentIndex - 1 + this.buttons.length) % this.buttons.length;
        break;
      case "Home":
        event.preventDefault();
        nextIndex = 0;
        break;
      case "End":
        event.preventDefault();
        nextIndex = this.buttons.length - 1;
        break;
      default:
        return;
    }

    this.buttons[nextIndex].focus();
    this.buttons[nextIndex].click();
  }
}

if (!customElements.get("megamenu-component")) {
  customElements.define("megamenu-component", MegamenuComponent);
}
